import { Avatar, Card, Grid } from "@mui/material";
import AccessDenied from "components/AccessDenied";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import TableSearch from "components/TableSearch";
import DataTable from "examples/Tables/DataTable";
import { useMemo, useState } from "react";
import appLogsService from "services/app-logs-service";

function formatDateTime(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function UserLogsList({ canAccess }) {
  const [users, setUsers] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  const columns = useMemo(
    () => [
      {
        Header: "User",
        accessor: "user",
        width: "30%",
      },
      {
        Header: "Email",
        accessor: "email",
        width: "22%",
      },
      {
        Header: "Uploads",
        accessor: "uploadsCount",
        align: "center",
      },
      {
        Header: "Latest Upload",
        accessor: "latestUploadAt",
        width: "20%",
      },
      {
        Header: "Device",
        accessor: "device",
        width: "28%",
      },
    ],
    [],
  );

  const rows = useMemo(() => {
    if (!users) {
      return [{ user: "Loading..." }];
    }

    if (!users.length) {
      return [{ user: "No uploaded logs were found." }];
    }

    return users.map((user) => {
      const displayName = user.userName || user.userId;
      const deviceInfo = [user.deviceName, user.deviceOS]
        .filter(Boolean)
        .join(" | ");

      return {
        data: user,
        user: (
          <MDBox display="flex" alignItems="center" gap={2}>
            <Avatar
              sx={{ width: 36, height: 36 }}
              src={user.profileImage || undefined}
            >
              {displayName?.[0] || "U"}
            </Avatar>
            <MDBox>
              <MDTypography variant="button" fontWeight="medium">
                {displayName}
              </MDTypography>
            </MDBox>
          </MDBox>
        ),
        email: (
          <MDTypography variant="caption" color="text">
            {user.userEmail || "-"}
          </MDTypography>
        ),
        uploadsCount: (
          <MDTypography variant="button">{user.uploadsCount || 0}</MDTypography>
        ),
        latestUploadAt: (
          <MDTypography variant="caption" color="text">
            {formatDateTime(user.latestUploadAt)}
          </MDTypography>
        ),
        device: (
          <MDTypography variant="caption" color="text">
            {deviceInfo || user.deviceId || "-"}
          </MDTypography>
        ),
      };
    });
  }, [users]);

  if (!canAccess || errorStatus === 403) {
    return <AccessDenied type="component" />;
  }

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <MDTypography variant="h6" color="white">
                User Logs {users ? `(${users.length})` : ""}
              </MDTypography>
            </MDBox>

            <MDBox p={2} mt={2}>
              <TableSearch
                id="user-logs-users"
                internalLoading={!Boolean(users) && !errorStatus}
                fullWidth
                setData={setUsers}
                onSearch={async (request, callback) => {
                  const search = request.autocomplete?.trim();
                  const result = await appLogsService.getUsers(
                    search ? { search } : null,
                  );
                  const [response] = result;

                  setErrorStatus(response.ok ? null : response.status);
                  callback(result);
                }}
                label="Search users"
              />
            </MDBox>

            {errorStatus && errorStatus !== 403 ? (
              <MDBox p={3}>
                <MDTypography color="error">
                  Unable to load user logs right now.
                </MDTypography>
              </MDBox>
            ) : (
              <MDBox>
                <DataTable
                  loading={!Boolean(users)}
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                  rowOptions={{
                    to: (row) => (row?.userId ? `/user-logs/${row.userId}` : ""),
                  }}
                />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
